import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/apiService';
import { Helmet } from 'react-helmet';

/**
 * TypePricingList – Accordion-Ansicht:
 * - Alle MachineTypes als Panels
 * - Beim Aufklappen lädt die Liste /typepricings/:machineTypeId
 * - Inline-Edit pro Zeile, Add/Delete wie bisher – nur pro Typ separat
 * - Sortierung: erst nach Einheitenname (Tag/Stunde), dann minQuantity
 */
const TypePricingList = () => {
  // Meta
  const [machineTypes, setMachineTypes] = useState([]);
  const [units, setUnits] = useState([]);
  const allowedUnitNames = useMemo(() => ['Tag', 'Stunde'], []);

  // Accordion-State
  const [expanded, setExpanded] = useState(() => new Set());            // typeIds die offen sind
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [loadingTypes, setLoadingTypes] = useState(() => new Set());    // typeIds die gerade laden

  // Daten pro Typ
  const [pricesByType, setPricesByType] = useState({});                 // { [typeId]: Price[] }
  const [copyByType, setCopyByType] = useState({});

  // Add-Form pro Typ
  const [newByType, setNewByType] = useState({});                       // { [typeId]: {min, max, price, unitId} }

  // Edit-Status global (id-eindeutig)
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ minQuantity: '', maxQuantity: '', pricePerUnit: '', unitId: '' });

    // Aus den geladenen Typen eine sortierte Sicht ableiten
   const sortedMachineTypes = useMemo(() => {
    return [...machineTypes].sort((a, b) =>
      (a?.name || '').localeCompare(b?.name || '', 'de', {
        numeric: true,
        ignorePunctuation: true,
        sensitivity: 'base',
      })
    );
  }, [machineTypes]);

  // ---------- Helpers ----------


  const setCopyField = (typeId, patch) => {
  setCopyByType(prev => ({
    ...prev,
    [typeId]: { mode: 'append', fromTypeId: null, ...(prev[typeId] || {}), ...patch }
  }));
};
    // Sortier-Helfer: Typen nach Name (inkl. Umlaute/Zahlen) sortieren
  const byName = (a, b) =>
    (a?.name || '').localeCompare(b?.name || '', 'de', {
      numeric: true,
      ignorePunctuation: true,
      sensitivity: 'base',
    });


  const setNewField = (typeId, patch) => {
    setNewByType(prev => ({
      ...prev,
      [typeId]: { ...(prev[typeId] || {}), ...patch }
    }));
  };

  const clearNewFields = (typeId) => {
    setNewByType(prev => ({ ...prev, [typeId]: { minQuantity: '', maxQuantity: '', pricePerUnit: '', unitId: '' } }));
  };

  const sortPrices = (list) => {
    return [...list].sort((a, b) => {
      const nameA = units.find(u => u.id === a.unitId)?.name || '';
      const nameB = units.find(u => u.id === b.unitId)?.name || '';
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
      return (a.minQuantity ?? 0) - (b.minQuantity ?? 0);
    });
  };

  const openPanel = async (typeId) => {
    if (expanded.has(typeId)) {
      // einklappen
      const next = new Set(expanded);
      next.delete(typeId);
      setExpanded(next);
      return;
    }
    // aufklappen
    const next = new Set(expanded);
    next.add(typeId);
    setExpanded(next);

    // lazy load prices (nur wenn noch nicht geladen)
    if (!pricesByType[typeId]) {
      await reloadPrices(typeId);
    }
  };

  const reloadPrices = async (typeId) => {
    if (!typeId) return;
    setLoadingTypes(prev => new Set(prev).add(typeId));
    try {
      const res = await api.get(`/typepricings/${typeId}`);
      setPricesByType(prev => ({ ...prev, [typeId]: res.data || [] }));
    } catch (err) {
      console.error('Error loading prices:', err?.response?.data || err);
      alert('Fehler beim Laden der Preisstaffeln');
    } finally {
      setLoadingTypes(prev => {
        const n = new Set(prev);
        n.delete(typeId);
        return n;
      });
    }
  };

  // ---------- Meta laden ----------
  useEffect(() => {
    const loadMeta = async () => {
      setLoadingMeta(true);
      try {
        const [mtRes, uRes] = await Promise.all([
          api.get('/machine-types'),
          api.get('/units')
        ]);
        setMachineTypes(mtRes.data || []);
        // Nur Tag/Stunde zulassen (wie bei dir)
        setUnits((uRes.data || []).filter(u => allowedUnitNames.includes(u.name)));
      } catch (err) {
        console.error('Meta load error:', err);
        alert('Fehler beim Laden der Stammdaten');
      } finally {
        setLoadingMeta(false);
      }
    };
    loadMeta();
  }, [allowedUnitNames]);

  // ---------- CRUD ----------


  const handleCopy = async (toTypeId) => {
  const cfg = copyByType[toTypeId] || {};
  const fromMachineTypeId = Number(cfg.fromTypeId || 0);
  const mode = cfg.mode || 'append';

  if (!fromMachineTypeId) {
    return alert('Bitte einen Quell-Typ auswählen.');
  }
  if (fromMachineTypeId === Number(toTypeId)) {
    return alert('Quelle und Ziel dürfen nicht identisch sein.');
  }

  try {
    const res = await api.post('/typepricings/copy', {
      fromMachineTypeId,
      toMachineTypeId: Number(toTypeId),
      mode,
    });
    // Nach Kopie Liste neu laden
    await reloadPrices(toTypeId);
    alert(`Kopiert: ${res.data?.copied ?? 0} Einträge (${mode}).`);
  } catch (err) {
    console.error('Error copying typepricings:', err?.response?.data || err);
    alert('Fehler beim Kopieren.');
  }
};



  const handleAdd = async (typeId) => {
    const draft = newByType[typeId] || {};
    const { minQuantity, maxQuantity, pricePerUnit, unitId } = draft;

    if (minQuantity === '' || maxQuantity === '' || pricePerUnit === '' || !unitId) {
      return alert('Bitte alle Felder ausfüllen!');
    }

    try {
      await api.post('/typepricings', {
        machineTypeId: Number(typeId),
        unitId:        Number(unitId),
        minQuantity:   Number(minQuantity),
        maxQuantity:   Number(maxQuantity),
        pricePerUnit:  Number(pricePerUnit)
      });
      clearNewFields(typeId);
      await reloadPrices(typeId);
    } catch (err) {
      console.error('Error adding pricing:', err?.response?.data || err);
      alert('Fehler beim Hinzufügen');
    }
  };

  const startEdit = (price) => {
    setEditingId(price.id);
    setEditDraft({
      minQuantity:  price.minQuantity,
      maxQuantity:  price.maxQuantity,
      pricePerUnit: price.pricePerUnit,
      unitId:       price.unitId
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft({ minQuantity: '', maxQuantity: '', pricePerUnit: '', unitId: '' });
  };

  const handleUpdate = async (typeId, id) => {
    const { minQuantity, maxQuantity, pricePerUnit, unitId } = editDraft;
    if (minQuantity === '' || maxQuantity === '' || pricePerUnit === '' || !unitId) {
      return alert('Bitte alle Felder befüllen!');
    }
    try {
      await api.put(`/typepricings/${id}`, {
        machineTypeId: Number(typeId),
        unitId:        Number(unitId),
        minQuantity:   Number(minQuantity),
        maxQuantity:   Number(maxQuantity),
        pricePerUnit:  Number(pricePerUnit)
      });
      cancelEdit();
      await reloadPrices(typeId);
    } catch (err) {
      console.error('Error updating pricing:', err?.response?.data || err);
      alert('Fehler beim Aktualisieren');
    }
  };

  const handleDelete = async (typeId, id) => {
    if (!window.confirm('Soll dieser Eintrag gelöscht werden?')) return;
    try {
      await api.delete(`/typepricings/${id}`);
      // lokal entfernen, ohne reload
      setPricesByType(prev => ({
        ...prev,
        [typeId]: (prev[typeId] || []).filter(p => p.id !== id)
      }));
    } catch (err) {
      console.error('Error deleting pricing:', err?.response?.data || err);
      alert('Fehler beim Löschen');
    }
  };

  const expandAll = async () => {
    const all = new Set(sortedMachineTypes.map(m => m.id));
    setExpanded(all);
    // Lazy load für alle, die noch nicht geladen sind
    const toLoad = sortedMachineTypes.map(m => m.id).filter(id => !pricesByType[id]);
    for (const id of toLoad) {
      // nacheinander laden, um request-flut zu vermeiden
      /* eslint-disable no-await-in-loop */
      await reloadPrices(id);
    }
  };

  const collapseAll = () => {
    setExpanded(new Set());
  };

  if (loadingMeta) return <div style={{ padding: 20 }}>Lade Stammdaten…</div>;

  // PDF-Download pro Typ – analog ServiceMarkerForm
const handleDownloadPDF = async (typeId) => {
  const pdfTab = window.open('', '_blank');

  try {
    const response = await api.get(`/typepricings/${typeId}/pdf`, {
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/pdf' });
    const url = window.URL.createObjectURL(blob);

    if (pdfTab) {
      pdfTab.location.href = url;
    } else {
      window.open(url, '_blank');
    }

    setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  } catch (error) {
    if (pdfTab) pdfTab.close();
    console.error('Typpreisliste konnte nicht geoeffnet werden:', error);
    alert('Typpreisliste konnte nicht geoeffnet werden.');
  }
};

  return (
    <div style={{ padding: '20px', maxWidth: '1100px', margin: 'auto' }}>
      <Helmet>
        <title>Typpreislisten – alle Typen</title>
        <meta name="description" content="Typpreislisten bearbeiten – Accordion je Maschinentyp" />
      </Helmet>

      <h2 style={{ marginBottom: 8 }}>Typpreislisten verwalten (alle Typen)</h2>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button
          onClick={expandAll}
          style={{ padding: '6px 10px', background: '#007bff', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
        >
          Alle aufklappen
        </button>
        <button
          onClick={collapseAll}
          style={{ padding: '6px 10px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
        >
          Alle einklappen
        </button>
      </div>

      <div>
        {sortedMachineTypes.map((mt) => {
          const isOpen = expanded.has(mt.id);
          const isLoading = loadingTypes.has(mt.id);
          const list = pricesByType[mt.id] ? sortPrices(pricesByType[mt.id]) : [];

          const addDraft = newByType[mt.id] || { minQuantity: '', maxQuantity: '', pricePerUnit: '', unitId: '' };

          return (
            <div key={mt.id} style={{ border: '0px solid #ddd', borderRadius: 0, marginBottom: 12, overflow: 'hidden' }}>
              <button
                onClick={() => openPanel(mt.id)}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: '10px 14px',
                  background: '#D4D4D4',
                  color: '#534e4eff',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontWeight: 600
                }}
              >
                <span>{mt.name}</span>
                <span></span>
              </button>

              {isOpen && (
                <div style={{ padding: 12, background: '#fff' }}>

                    {/* Toolbar: PDF-Export */}
  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
    <button
      onClick={() => handleDownloadPDF(mt.id)}
      style={{ padding: '6px 12px', background: '#007bff', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
      title="Preisliste (dieser Typ) als PDF herunterladen"
    >
      PDF Preisliste
    </button>
  </div>


                {/* Copy-Row */}
<div style={{
  display: 'grid',
  gridTemplateColumns: '1fr 1fr auto',
  gap: 10,
  marginBottom: 10,
  alignItems: 'center',
  background: '#f7f7f7',
  padding: '8px 10px',
  borderRadius: 4
}}>
  <select
    value={(copyByType[mt.id]?.fromTypeId) || ''}
    onChange={e => setCopyField(mt.id, { fromTypeId: e.target.value ? Number(e.target.value) : null })}
    style={{ height: 32 }}
    title="Quelle (von diesem Typ kopieren)"
  >
    <option value="">Quelle wählen…</option>
    {sortedMachineTypes
      .filter(t => t.id !== mt.id)
      .map(t => (
        <option key={t.id} value={t.id}>{t.name}</option>
      ))}
  </select>

  <div style={{ display: 'flex', gap: 8 }}>
    <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft:'20px'  }}>
      <input
        type="radio"
        name={`copy-mode-${mt.id}`}
        checked={(copyByType[mt.id]?.mode || 'append') === 'append'}
        onChange={() => setCopyField(mt.id, { mode: 'append' })}
      />
      ergänzen
    </label>
    <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <input
        type="radio"
        name={`copy-mode-${mt.id}`}
        checked={(copyByType[mt.id]?.mode || 'append') === 'replace'}
        onChange={() => setCopyField(mt.id, { mode: 'replace' })}
      />
      ersetzen
    </label>
  </div>

  <button
    onClick={() => handleCopy(mt.id)}
    style={{ padding: '6px 12px', background: '#17a2b8', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
    title="Preisstaffeln vom gewählten Quell-Typ in diesen Typ kopieren"
  >
    Kopieren
  </button>
</div>

                  {/* Add Form */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr auto', gap: 10, marginBottom: 14, alignItems: 'center' }}>
                    <input
                      type="number" placeholder="Min Menge"
                      value={addDraft.minQuantity}
                      onChange={e => setNewField(mt.id, { minQuantity: e.target.value })}
                    />
                    <input
                      type="number" placeholder="Max Menge"
                      value={addDraft.maxQuantity}
                      onChange={e => setNewField(mt.id, { maxQuantity: e.target.value })}
                    />
                    <input
                      type="number" step="0.01" placeholder="Preis/Einheit"
                      value={addDraft.pricePerUnit}
                      onChange={e => setNewField(mt.id, { pricePerUnit: e.target.value })}
                    />
                    <select
                      value={addDraft.unitId || ''}
                      onChange={e => setNewField(mt.id, { unitId: e.target.value })}
                      style={{
                      width: '70%',
                      marginLeft: '0px',
                      marginTop: '0px',
                  
                      }}
                    >
                      <option value="" disabled>Einheit wählen</option>
                      {units.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                    <button
                      onClick={() => handleAdd(mt.id)}
                      style={{ padding: '6px 12px', background: '#28a745', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', marginBottom: '12px', }}
                    >
                      Hinzufügen
                    </button>
                  </div>

                  {isLoading ? (
                    <div style={{ padding: 8 }}>Lade Preisstaffeln…</div>
                  ) : (
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      {list.map(p => (
                        <li
                          key={p.id}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '220px 220px 220px 220px 220px 220px',
                            gap: 10,
                            padding: '8px 0',
                            borderBottom: '1px solid #eee',
                            alignItems: 'center'
                          }}
                        >
                          {editingId === p.id ? (
                            <>
                              <input
                                type="number"
                                value={editDraft.minQuantity}
                                onChange={e => setEditDraft({ ...editDraft, minQuantity: e.target.value })}
                              />
                              <input
                                type="number"
                                value={editDraft.maxQuantity}
                                onChange={e => setEditDraft({ ...editDraft, maxQuantity: e.target.value })}
                              />
                              <input
                                type="number"
                                step="0.01"
                                value={editDraft.pricePerUnit}
                                onChange={e => setEditDraft({ ...editDraft, pricePerUnit: e.target.value })}
                              />
                              <select
                                value={editDraft.unitId || ''}
                                onChange={e => setEditDraft({ ...editDraft, unitId: e.target.value })}
                                style={{ marginTop: 0,marginLeft: 0  }}
                              >
                                <option value="" disabled>Einheit wählen</option>
                                {units.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                              </select>
                              <div>
                                <button onClick={() => handleUpdate(mt.id, p.id)} style={{ marginLeft: -50 }}>✅</button>
                                <button onClick={cancelEdit} style={{marginLeft: 5  }}>✖️</button>
                              </div>
                            </>
                          ) : (
                            <>
                              <span>{p.minQuantity}</span>
                              <span>{p.maxQuantity}</span>
                              <span>{p.pricePerUnit}</span>
                              <span>{units.find(u => u.id === p.unitId)?.name}</span>
                              <div style={{ display: 'flex', gap: 6 }}>
                                <button
                                  onClick={() => startEdit(p)}
                                  style={{ background: '#007bff', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 8px', cursor: 'pointer' }}
                                >
                                  edit
                                </button>
                                <button
                                  onClick={() => handleDelete(mt.id, p.id)}
                                  style={{ background: '#dc3545', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 8px', cursor: 'pointer' }}
                                >
                                  X
                                </button>
                              </div>
                            </>
                          )}
                        </li>
                      ))}
                      {list.length === 0 && (
                        <li style={{ padding: '6px 2px', color: '#6c757d' }}>Keine Preisstaffeln vorhanden.</li>
                      )}
                    </ul>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TypePricingList;
